export interface IborPortfolioPosition {
  quantity?: number | string | null;
  marketValue?: number | string | null;
  assetClassName?: string | null;
  assetSubClassName?: string | null;
  cusip?: string | null;
  securityName?: string | null;
}

export interface PortfolioAllocation {
  label: string;
  marketValue: number;
  percentage: number;
}

export interface PortfolioMetrics {
  netAssetValue: number;
  assetsUnderManagement: number;
  positionCount: number;
  allocations: PortfolioAllocation[];
}

const toFiniteNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatAssetClass = (raw?: string | null): string => {
  if (!raw) return 'Unclassified';

  const normalized = raw.toUpperCase().trim();
  const labels: Record<string, string> = {
    CASH: 'Cash',
    FIXED_INCOME: 'Fixed Income',
    'FIXED INCOME': 'Fixed Income',
    PRIVATE_CREDIT: 'Private Credit',
    'PRIVATE CREDIT': 'Private Credit',
    LEVERAGED_LOAN: 'Leveraged Loans',
    'LEVERAGED LOAN': 'Leveraged Loans',
    LEVERAGED_LOANS: 'Leveraged Loans',
    'LEVERAGED LOANS': 'Leveraged Loans',
    REAL_ESTATE: 'Real Estate',
    REAL_ESTATE_DEBT: 'Real Estate Debt',
    REAL_ESTATE_EQUITY: 'Real Estate Equity',
    PRIVATE_EQUITY: 'Private Equity',
    CLO: 'CLO',
    CLO_DEBT: 'CLO Debt',
    CLO_EQUITY: 'CLO Equity',
    DISTRESSED: 'Distressed',
    EQUITY: 'Equity',
    DERIVATIVE: 'Derivatives',
    DERIVATIVES: 'Derivatives',
    FX: 'FX',
    REPO: 'Repo',
    CMBS: 'CMBS',
    RMBS: 'RMBS',
    ABF: 'ABF',
    MUNICIPAL: 'Municipal',
    US_TREASURY: 'US Treasuries',
    US_TREASURIES: 'US Treasuries',
  };

  return labels[normalized]
    || raw
      .trim()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
};

export const isActivePosition = (position: IborPortfolioPosition): boolean =>
  toFiniteNumber(position.quantity) !== 0 || toFiniteNumber(position.marketValue) !== 0;

export const calculatePortfolioMetrics = (
  positions: IborPortfolioPosition[],
): PortfolioMetrics => {
  const activePositions = positions.filter(isActivePosition);
  const netAssetValue = activePositions.reduce(
    (total, position) => total + toFiniteNumber(position.marketValue),
    0,
  );
  const assetsUnderManagement = activePositions.reduce(
    (total, position) => total + Math.abs(toFiniteNumber(position.marketValue)),
    0,
  );

  const allocationByAssetClass = new Map<string, number>();
  activePositions.forEach(position => {
    const marketValue = Math.abs(toFiniteNumber(position.marketValue));
    if (marketValue === 0) return;

    const label = formatAssetClass(position.assetClassName || position.assetSubClassName);
    allocationByAssetClass.set(
      label,
      (allocationByAssetClass.get(label) || 0) + marketValue,
    );
  });

  const allocations = Array.from(allocationByAssetClass.entries())
    .map(([label, marketValue]) => ({
      label,
      marketValue,
      percentage: assetsUnderManagement === 0
        ? 0
        : (marketValue / assetsUnderManagement) * 100,
    }))
    .sort((left, right) => right.marketValue - left.marketValue);

  return {
    netAssetValue,
    assetsUnderManagement,
    positionCount: activePositions.length,
    allocations,
  };
};

export const calculatePositionWeight = (
  position: IborPortfolioPosition,
  assetsUnderManagement: number,
): number => {
  if (assetsUnderManagement === 0) return 0;
  return (Math.abs(toFiniteNumber(position.marketValue)) / assetsUnderManagement) * 100;
};
